import { MoreVerticalIcon, EditIcon, EyeIcon, TrashIcon } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Question } from "@/components/academician/question-editor"

export type QuizStatus = "published" | "draft"

export interface Quiz {
  id: string
  title: string
  description?: string
  questionsCount: number
  passingScore: number
  assignedTo?: string
  status: QuizStatus
  createdAt?: string
  updatedAt?: string
  questions?: Question[]
}

interface QuizTableRowProps {
  quiz: Quiz
  onEdit?: (quiz: Quiz) => void
  onPreview?: (quiz: Quiz) => void
  onDelete?: (quiz: Quiz) => void
}

export function QuizTableRow({ quiz, onEdit, onPreview, onDelete }: QuizTableRowProps) {
  const statusColors = {
    published: "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20",
    draft: "bg-muted text-muted-foreground border-border",
  }

  return (
    <tr className="border-b border-border hover:bg-muted/50 transition-colors">
      <td className="px-6 py-4">
        <div className="font-medium text-foreground">{quiz.title}</div>
        {quiz.description && (
          <div className="text-sm text-muted-foreground mt-1 line-clamp-1">
            {quiz.description}
          </div>
        )}
      </td>
      <td className="px-6 py-4 text-center">
        <span className="text-foreground font-medium">{quiz.questionsCount}</span>
      </td>
      <td className="px-6 py-4 text-center">
        <span className="text-foreground font-medium">{quiz.passingScore}%</span>
      </td>
      <td className="px-6 py-4">
        {quiz.assignedTo ? (
          <span className="text-sm text-foreground">{quiz.assignedTo}</span>
        ) : (
          <span className="text-sm text-muted-foreground italic">Not assigned</span>
        )}
      </td>
      <td className="px-6 py-4">
        <Badge className={statusColors[quiz.status]}>
          {quiz.status === "published" ? "Published" : "Draft"}
        </Badge>
      </td>
      <td className="px-6 py-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreVerticalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit?.(quiz)}>
              <EditIcon className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPreview?.(quiz)}>
              <EyeIcon className="h-4 w-4 mr-2" />
              Preview
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete?.(quiz)}
              className="text-destructive focus:text-destructive"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  )
}